import * as IF from "infrontjs";
import { getMaxHearts, getLivesState, getNextRefillMs, formatRefillTimer, refillAllHearts } from '../data/lives.js';
import { getRankForLevel } from '../data/ranks.js';
import { getCoins, spendCoins } from '../data/coins.js';
import { canInstall, isInstalled, triggerInstall, onInstallAvailabilityChange } from '../data/pwa.js';
import { trackPageView } from '../data/analytics.js';
import { getItem } from '../data/storage.js';

const REFILL_HEARTS_COIN_PRICE = 500;

const HEART_FULL_SVG = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <defs>
        <radialGradient id="hfGrad" cx="0.35" cy="0.3" r="0.8">
            <stop offset="0" stop-color="#F17A67"/>
            <stop offset="0.6" stop-color="#E34F3A"/>
            <stop offset="1" stop-color="#B03324"/>
        </radialGradient>
    </defs>
    <path d="M12 21.2 C8.2 18.4 3 14.8 3 9.8 C3 7.15 5.15 5 7.8 5 C9.6 5 11.05 6 12 7.6 C12.95 6 14.4 5 16.2 5 C18.85 5 21 7.15 21 9.8 C21 14.8 15.8 18.4 12 21.2 Z" fill="url(#hfGrad)"/>
    <path d="M6.5 8.5 C7.2 7.4 8.3 6.8 9.4 7.1" stroke="#FAF4E6" stroke-width="1.4" stroke-linecap="round" fill="none" opacity="0.85"/>
</svg>`;

const HEART_EMPTY_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="rgba(250,244,230,0.4)" stroke-width="2" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21.2 C8.2 18.4 3 14.8 3 9.8 C3 7.15 5.15 5 7.8 5 C9.6 5 11.05 6 12 7.6 C12.95 6 14.4 5 16.2 5 C18.85 5 21 7.15 21 9.8 C21 14.8 15.8 18.4 12 21.2 Z"/>
</svg>`;

const LOGO_SVG = `<svg viewBox="0 0 96 96" xmlns="http://www.w3.org/2000/svg">
    <defs>
        <radialGradient id="pmHi" cx="0.35" cy="0.3" r="0.7">
            <stop offset="0" stop-color="#B45D5A"/>
            <stop offset="1" stop-color="#8C4846"/>
        </radialGradient>
    </defs>
    <rect x="4" y="4" width="88" height="88" rx="22" fill="url(#pmHi)"/>
    <rect x="5.5" y="5.5" width="85" height="85" rx="20.5" fill="none" stroke="rgba(255,255,255,0.12)" stroke-width="1"/>
    <circle cx="48" cy="54" r="22" fill="#2D2520"/>
    <ellipse cx="40" cy="46" rx="6" ry="4" fill="#FAF4E6" opacity="0.85"/>
    <path d="M52 34 Q58 26 64 28" stroke="#F2E9D8" stroke-width="2.6" fill="none" stroke-linecap="round"/>
    <circle cx="66" cy="27" r="4" fill="#F2B544"/>
    <circle cx="66" cy="27" r="2" fill="#FAF4E6"/>
    <ellipse cx="48" cy="78" rx="18" ry="3" fill="rgba(40,20,18,0.3)"/>
</svg>`;

const FULLSCREEN_SVG_ON = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9 V6 a2 2 0 0 1 2 -2 h3"/><path d="M20 9 V6 a2 2 0 0 0 -2 -2 h-3"/><path d="M4 15 v3 a2 2 0 0 0 2 2 h3"/><path d="M20 15 v3 a2 2 0 0 1 -2 2 h-3"/></svg>`;
const FULLSCREEN_SVG_OFF = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6 L18 18"/><path d="M18 6 L6 18"/></svg>`;

const STAR_SVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.8 L14.6 8.9 L21.1 9.6 L16.2 13.9 L17.7 20.3 L12 16.9 L6.3 20.3 L7.8 13.9 L2.9 9.6 L9.4 8.9 Z"/></svg>`;

const TIMER_SVG = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><circle cx="12" cy="13" r="7.5"/><path d="M12 9 V13 L14.5 14.5"/><path d="M10 3 H14"/></svg>`;

const ARROW_SVG = `<svg class="pm-play__arrow" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6 L15 12 L9 18"/></svg>`;

const REFILL_HEART_BTN_SVG = `<svg class="pm-play__heartIcon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 21 C5 16 1.5 12 1.5 7.5 C1.5 4.8 3.6 2.7 6.3 2.7 C8.4 2.7 10.5 4 12 6 C13.5 4 15.6 2.7 17.7 2.7 C20.4 2.7 22.5 4.8 22.5 7.5 C22.5 12 19 16 12 21 Z"/></svg>`;

const REFILL_COIN_SVG = `<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" width="16" height="16" aria-hidden="true">
    <defs>
        <radialGradient id="pmCoinFaceMini" cx="0.34" cy="0.28" r="0.95">
            <stop offset="0" stop-color="#FFE3A8"/>
            <stop offset="0.45" stop-color="#F2B544"/>
            <stop offset="1" stop-color="#A87018"/>
        </radialGradient>
    </defs>
    <circle cx="16" cy="16" r="14" fill="#7A5212"/>
    <circle cx="16" cy="15.4" r="11.6" fill="url(#pmCoinFaceMini)"/>
</svg>`;

const REFILL_HERO_SVG = `<svg viewBox="0 0 120 120" width="84" height="84" aria-hidden="true">
    <defs>
        <radialGradient id="rh-grad" cx="0.35" cy="0.3" r="0.75">
            <stop offset="0" stop-color="#F17A67"/>
            <stop offset="0.6" stop-color="#E34F3A"/>
            <stop offset="1" stop-color="#B03324"/>
        </radialGradient>
    </defs>
    <path d="M60 104 C30 84 8 64 8 40 C8 26 20 14 34 14 C44 14 54 20 60 30 C66 20 76 14 86 14 C100 14 112 26 112 40 C112 64 90 84 60 104 Z" fill="url(#rh-grad)"/>
    <path d="M28 34 C32 28 40 24 46 26" stroke="#FAF4E6" stroke-width="3.5" fill="none" stroke-linecap="round" opacity="0.85"/>
    <g fill="#F2B544"><path d="M90 48 l3 6 6 1 -4.5 4 1 6 -5.5 -3 -5.5 3 1 -6 -4.5 -4 6 -1 z"/></g>
</svg>`;

const REFILL_OK_ARROW_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6 L15 12 L9 18"/></svg>`;

const MENU_SVG = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M4 7 H20"/><path d="M4 12 H20"/><path d="M4 17 H20"/></svg>`;
const SIDE_CLOSE_SVG = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M6 6 L18 18"/><path d="M18 6 L6 18"/></svg>`;
const SIDE_CHEV_SVG = `<svg class="pm-side__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6 L15 12 L9 18"/></svg>`;
const SIDE_ICON_STATS = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 20 V12"/><path d="M10 20 V4"/><path d="M16 20 V14"/><path d="M22 20 V8"/></svg>`;
const SIDE_ICON_SETTINGS = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1A2 2 0 1 1 4.3 17l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1A2 2 0 1 1 7 4.3l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></svg>`;
const SIDE_ICON_HELP = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M9.2 9.5 a 3 3 0 1 1 4.3 2.7 c -1 0.5 -1.5 1.1 -1.5 2.3"/><circle cx="12" cy="17.5" r="0.6" fill="currentColor"/></svg>`;
const SIDE_ICON_INSTALL = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4 V15"/><path d="M7 11 L12 16 L17 11"/><path d="M5 20 H19"/></svg>`;

const COIN_SVG = `<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" width="22" height="22">
    <defs>
        <radialGradient id="pmCoinFace" cx="0.34" cy="0.28" r="0.95">
            <stop offset="0" stop-color="#FFE3A8"/>
            <stop offset="0.45" stop-color="#F2B544"/>
            <stop offset="1" stop-color="#A87018"/>
        </radialGradient>
        <linearGradient id="pmCoinRim" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="#D89A2C"/>
            <stop offset="1" stop-color="#7A5212"/>
        </linearGradient>
    </defs>
    <ellipse cx="16" cy="17.5" rx="14" ry="13.5" fill="rgba(40,20,18,0.25)"/>
    <circle cx="16" cy="16" r="14" fill="url(#pmCoinRim)"/>
    <circle cx="16" cy="16" r="14" fill="none" stroke="#5A3A0E" stroke-width="0.8" opacity="0.6"/>
    <circle cx="16" cy="15.4" r="11.6" fill="url(#pmCoinFace)"/>
    <circle cx="16" cy="15.4" r="11.6" fill="none" stroke="#8B5B10" stroke-width="0.8" opacity="0.4"/>
    <g transform="translate(16 16.2)">
        <circle r="5" fill="#3A2A20"/>
        <circle cx="-1.6" cy="-1.6" r="1.4" fill="#7A5C44" opacity="0.85"/>
        <g stroke="#3A2A20" stroke-width="1.5" stroke-linecap="round">
            <line x1="0" y1="-6.4" x2="0" y2="-7.8"/>
            <line x1="0" y1="6.4" x2="0" y2="7.8"/>
            <line x1="-6.4" y1="0" x2="-7.8" y2="0"/>
            <line x1="6.4" y1="0" x2="7.8" y2="0"/>
            <line x1="-4.6" y1="-4.6" x2="-5.6" y2="-5.6"/>
            <line x1="4.6" y1="4.6" x2="5.6" y2="5.6"/>
            <line x1="-4.6" y1="4.6" x2="-5.6" y2="5.6"/>
            <line x1="4.6" y1="-4.6" x2="5.6" y2="-5.6"/>
        </g>
        <circle cx="3.2" cy="-3.2" r="0.8" fill="#FFE3A8"/>
    </g>
    <ellipse cx="11.5" cy="9.5" rx="4.2" ry="2" fill="#FFF1BE" opacity="0.55"/>
</svg>`;

function formatCoins( n )
{
    const v = Math.max( 0, Math.floor( n ) );
    if ( v > 99999 ) return Math.floor( v / 1000 ) + 'k';
    return String( v ).padStart( 5, '0' );
}

function renderHeartsRow( hearts, maxHearts )
{
    let html = '';
    for ( let i = 0; i < maxHearts; i++ )
    {
        const cls = i < hearts ? 'pm-hearts__icon' : 'pm-hearts__icon is-empty';
        html += `<span class="${ cls }">${ i < hearts ? HEART_FULL_SVG : HEART_EMPTY_SVG }</span>`;
    }
    return html;
}

export class StartState extends IF.State
{
    static ID = 'start';
    static ROUTE = '/';

    async enter()
    {
        trackPageView( 'StartState', '/' );

        const appEl = document.getElementById( 'app' );
        const gameEl = document.getElementById( 'game-container' );

        appEl.classList.add( 'active' );
        gameEl.classList.remove( 'active' );

        const cleared = parseInt( getItem( 'minesweeper_cleared' ) || '0', 10 );
        const lives = getLivesState();
        const maxHearts = getMaxHearts();
        const noLives = lives.hearts <= 0;
        const initialCoins = getCoins();
        const canRefill = noLives && initialCoins >= REFILL_HEARTS_COIN_PRICE;
        const rankInfo = getRankForLevel( cleared );

        let buttonLabel, eyebrow, headline;
        if ( cleared >= 999 )
        {
            buttonLabel = 'Play Again';
            eyebrow = 'All Clear';
            headline = '999 / 999';
        }
        else if ( cleared > 0 )
        {
            buttonLabel = 'Continue';
            eyebrow = 'Level';
            headline = String( cleared + 1 );
        }
        else
        {
            buttonLabel = 'Start';
            eyebrow = 'First Run';
            headline = 'Level 1';
        }

        const pct = Math.round( ( cleared / 999 ) * 100 );
        const refillMs = getNextRefillMs();
        const showTimer = lives.hearts < maxHearts && refillMs > 0;

        this.app.view.renderHtml( this.app.container, `
            <div class="pm-start">
                <div class="pm-start__ambient" aria-hidden="true">
                    <div class="pm-mine pm-mine--a"></div>
                    <div class="pm-mine pm-mine--b"></div>
                    <div class="pm-mine pm-mine--c"></div>
                </div>

                <div class="pm-start__top">
                    <button class="pm-ic-btn pm-ic-btn--menu" id="btn-menu" aria-label="Open menu" type="button">${ MENU_SVG }</button>
                    <button class="pm-coins" id="pm-coins" aria-label="${ getCoins() } coins" type="button">
                        <span class="pm-coins__icon">${ COIN_SVG }</span>
                        <span class="pm-coins__num" id="pm-coins-num">${ formatCoins( 0 ) }</span>
                    </button>
                    <button class="pm-ic-btn" id="btn-fullscreen" aria-label="Toggle fullscreen">${ FULLSCREEN_SVG_ON }</button>
                </div>

                <div class="pm-start__center">
                    <div class="pm-logo">
                        <div class="pm-logo__mark">${ LOGO_SVG }</div>
                        <div class="pm-logo__wordmark">Puzzle<span>mines</span></div>
                    </div>
                    <div class="pm-eyebrow">${ eyebrow }</div>
                    <div class="pm-headline">${ headline }</div>

                    <div class="pm-progress" aria-label="${ cleared } of 999 levels cleared">
                        <div class="pm-progress__track">
                            <div class="pm-progress__fill" style="width: ${ pct }%"></div>
                        </div>
                        <div class="pm-progress__caption">
                            <span class="pm-progress__count">${ cleared }</span>
                            <span class="pm-progress__total"> / 999 cleared</span>
                        </div>
                    </div>

                    <div class="pm-rank">
                        <span class="pm-rank__star">${ STAR_SVG }</span>
                        <span class="pm-rank__name">${ rankInfo.rank.name }</span>
                    </div>
                </div>

                <div class="pm-start__bottom">
                    <div class="pm-hearts" id="pm-hearts" aria-label="${ lives.hearts } of ${ maxHearts } lives">
                        ${ renderHeartsRow( lives.hearts, maxHearts ) }
                        <span class="pm-hearts__timer" id="pm-hearts-timer" style="${ showTimer ? '' : 'display:none' }">
                            ${ TIMER_SVG }
                            <span id="pm-hearts-timer-text">${ formatRefillTimer( refillMs ) }</span>
                        </span>
                    </div>

                    <div class="pm-start__action" id="pm-start-action">
                        ${ canRefill ? `
                            <button class="pm-play pm-play--refill" id="btn-refill" type="button" aria-label="Refill lives for ${ REFILL_HEARTS_COIN_PRICE } coins">
                                <span class="pm-play__label">${ REFILL_HEART_BTN_SVG }Refill Lives</span>
                                <span class="pm-play__cost">${ REFILL_COIN_SVG }${ REFILL_HEARTS_COIN_PRICE }</span>
                            </button>
                        ` : `
                            <button class="pm-play${ noLives ? ' is-disabled' : '' }" id="btn-start"${ noLives ? ' disabled' : '' }>
                                <span class="pm-play__label">${ noLives ? 'Out of Lives' : buttonLabel }</span>
                                ${ noLives ? '' : ARROW_SVG }
                            </button>
                        ` }
                    </div>

                    <div class="pm-hearts__refill-note" id="pm-refill-note" style="${ noLives && showTimer ? '' : 'display:none' }">
                        ${ canRefill
                            ? `or wait <strong id="pm-refill-note-time">${ formatRefillTimer( refillMs ) }</strong> for a free heart`
                            : `Next heart in <strong id="pm-refill-note-time">${ formatRefillTimer( refillMs ) }</strong>` }
                    </div>
                </div>

                <div class="pm-side" id="pm-side" aria-hidden="true">
                    <button class="pm-side__scrim" id="pm-side-scrim" aria-label="Close menu" tabindex="-1"></button>
                    <aside class="pm-side__panel" role="dialog" aria-modal="true" aria-label="Main menu">
                        <div class="pm-side__top">
                            <button class="pm-side__close" id="pm-side-close" type="button" aria-label="Close menu">${ SIDE_CLOSE_SVG }</button>
                            <div class="pm-side__logo">
                                <div class="pm-side__logoMark">${ LOGO_SVG }</div>
                                <div class="pm-side__wordmark">Puzzle<span>mines</span></div>
                            </div>
                        </div>

                        <nav class="pm-side__nav">
                            <button class="pm-side__item" type="button" data-side-item="stats">
                                <span class="pm-side__itemIcon">${ SIDE_ICON_STATS }</span>
                                <span class="pm-side__itemLabel">Stats</span>
                                ${ SIDE_CHEV_SVG }
                            </button>
                            <button class="pm-side__item" type="button" data-side-item="settings">
                                <span class="pm-side__itemIcon">${ SIDE_ICON_SETTINGS }</span>
                                <span class="pm-side__itemLabel">Settings</span>
                                ${ SIDE_CHEV_SVG }
                            </button>
                            <button class="pm-side__item" type="button" data-side-item="help">
                                <span class="pm-side__itemIcon">${ SIDE_ICON_HELP }</span>
                                <span class="pm-side__itemLabel">Help</span>
                                ${ SIDE_CHEV_SVG }
                            </button>
                        </nav>

                        <div class="pm-side__spacer"></div>

                        <button class="pm-side__install" id="pm-side-install" type="button" hidden>
                            ${ SIDE_ICON_INSTALL }
                            <span class="pm-side__installLabel">Install Puzzlemines</span>
                        </button>

                        <div class="pm-side__legal">
                            <button class="pm-side__legalLink" type="button" data-side-legal="imprint">Imprint</button>
                            <span class="pm-side__legalDot" aria-hidden="true">·</span>
                            <button class="pm-side__legalLink" type="button" data-side-legal="privacy">Data Privacy</button>
                            <span class="pm-side__legalDot" aria-hidden="true">·</span>
                            <button class="pm-side__legalLink" type="button" data-side-legal="tos">Terms of Service</button>
                            <span class="pm-side__legalDot" aria-hidden="true">·</span>
                            <button class="pm-side__legalLink" type="button" data-open-cookie-settings>Cookie Settings</button>
                        </div>
                    </aside>
                </div>

                <div class="pm-confirm pm-confirm--success pm-refill-dlg" id="pm-refill-dlg" aria-hidden="true">
                    <button class="pm-confirm__scrim" id="pm-refill-dlg-scrim" type="button" aria-label="Close" tabindex="-1"></button>
                    <div class="pm-confirm__card" role="dialog" aria-modal="true" aria-labelledby="pm-refill-dlg-title">
                        <div class="pm-refill-dlg__art" aria-hidden="true">
                            ${ REFILL_HERO_SVG }
                            <div class="pm-refill-dlg__ring"></div>
                        </div>
                        <h3 class="pm-confirm__title" id="pm-refill-dlg-title">Lives restored!</h3>
                        <p class="pm-confirm__body">
                            All <strong id="pm-refill-dlg-count">${ maxHearts }</strong> hearts are full again. Go clear some mines.
                        </p>
                        <div class="pm-refill-dlg__hearts" id="pm-refill-dlg-hearts" aria-hidden="true"></div>
                        <div class="pm-confirm__actions pm-confirm__actions--single">
                            <button class="pm-confirm__ok" id="pm-refill-dlg-ok" type="button">
                                ${ REFILL_OK_ARROW_SVG }Let's play
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `);

        const sideEl = document.getElementById( 'pm-side' );
        const openMenu = () =>
        {
            sideEl.classList.add( 'is-open' );
            sideEl.setAttribute( 'aria-hidden', 'false' );
        };
        const closeMenu = () =>
        {
            sideEl.classList.remove( 'is-open' );
            sideEl.setAttribute( 'aria-hidden', 'true' );
        };
        document.getElementById( 'btn-menu' ).addEventListener( 'click', openMenu );
        document.getElementById( 'pm-side-close' ).addEventListener( 'click', closeMenu );
        document.getElementById( 'pm-side-scrim' ).addEventListener( 'click', closeMenu );

        const statsItem = document.querySelector( '[data-side-item="stats"]' );
        if ( statsItem )
        {
            statsItem.addEventListener( 'click', () =>
            {
                closeMenu();
                this.app.router.redirect( '/stats' );
            });
        }

        const helpItem = document.querySelector( '[data-side-item="help"]' );
        if ( helpItem )
        {
            helpItem.addEventListener( 'click', () =>
            {
                closeMenu();
                this.app.router.redirect( '/help' );
            });
        }

        const settingsItem = document.querySelector( '[data-side-item="settings"]' );
        if ( settingsItem )
        {
            settingsItem.addEventListener( 'click', () =>
            {
                closeMenu();
                this.app.router.redirect( '/settings' );
            });
        }

        const legalRoutes = { imprint: '/imprint', privacy: '/privacy', tos: '/tos' };
        document.querySelectorAll( '[data-side-legal]' ).forEach( ( btn ) =>
        {
            const route = legalRoutes[ btn.getAttribute( 'data-side-legal' ) ];
            if ( !route ) return;
            btn.addEventListener( 'click', () =>
            {
                closeMenu();
                this.app.router.redirect( route );
            });
        });

        const installBtn = document.getElementById( 'pm-side-install' );
        if ( installBtn )
        {
            const refreshInstallBtn = () =>
            {
                installBtn.hidden = !( canInstall() && !isInstalled() );
            };
            refreshInstallBtn();
            this._unsubscribeInstall = onInstallAvailabilityChange( refreshInstallBtn );

            installBtn.addEventListener( 'click', async () =>
            {
                const outcome = await triggerInstall();
                refreshInstallBtn();
                if ( outcome === 'accepted' ) closeMenu();
            });
        }

        const startHandler = () =>
        {
            if ( getLivesState().hearts <= 0 ) return;
            this.app.router.redirect( '/game' );
        };
        const startBtn = document.getElementById( 'btn-start' );
        if ( startBtn ) startBtn.addEventListener( 'click', startHandler );

        // Refill-lives button: spend coins, refill all hearts, show success dialog,
        // then swap the action area to the standard Start button.
        const refillDlgEl = document.getElementById( 'pm-refill-dlg' );
        const refillDlgHeartsEl = document.getElementById( 'pm-refill-dlg-hearts' );
        const refillDlgCountEl = document.getElementById( 'pm-refill-dlg-count' );
        const closeRefillDialog = () =>
        {
            if ( refillDlgEl )
            {
                refillDlgEl.classList.remove( 'is-open' );
                refillDlgEl.setAttribute( 'aria-hidden', 'true' );
            }
        };
        const openRefillDialog = ( max ) =>
        {
            if ( !refillDlgEl ) return;
            if ( refillDlgCountEl ) refillDlgCountEl.textContent = String( max );
            if ( refillDlgHeartsEl )
            {
                let html = '';
                for ( let i = 0; i < max; i++ )
                {
                    html += `<span class="pm-refill-dlg__heart" style="animation-delay:${ 120 + i * 90 }ms">${ HEART_FULL_SVG }</span>`;
                }
                refillDlgHeartsEl.innerHTML = html;
            }
            refillDlgEl.classList.add( 'is-open' );
            refillDlgEl.setAttribute( 'aria-hidden', 'false' );
        };
        const refillDlgScrim = document.getElementById( 'pm-refill-dlg-scrim' );
        const refillDlgOk = document.getElementById( 'pm-refill-dlg-ok' );
        if ( refillDlgScrim ) refillDlgScrim.addEventListener( 'click', closeRefillDialog );
        if ( refillDlgOk ) refillDlgOk.addEventListener( 'click', closeRefillDialog );

        const refillBtn = document.getElementById( 'btn-refill' );
        if ( refillBtn )
        {
            refillBtn.addEventListener( 'click', () =>
            {
                if ( !spendCoins( REFILL_HEARTS_COIN_PRICE ) ) return;
                refillAllHearts();

                if ( this._coinAnimRaf )
                {
                    cancelAnimationFrame( this._coinAnimRaf );
                    this._coinAnimRaf = null;
                }
                const coinNumEl2 = document.getElementById( 'pm-coins-num' );
                if ( coinNumEl2 ) coinNumEl2.textContent = formatCoins( getCoins() );

                const max = getMaxHearts();
                const heartsEl = document.getElementById( 'pm-hearts' );
                if ( heartsEl )
                {
                    heartsEl.innerHTML = renderHeartsRow( max, max ) +
                        `<span class="pm-hearts__timer" id="pm-hearts-timer" style="display:none">${ TIMER_SVG }<span id="pm-hearts-timer-text"></span></span>`;
                }

                const noteEl = document.getElementById( 'pm-refill-note' );
                if ( noteEl ) noteEl.style.display = 'none';

                const actionEl = document.getElementById( 'pm-start-action' );
                if ( actionEl )
                {
                    actionEl.innerHTML = `
                        <button class="pm-play" id="btn-start">
                            <span class="pm-play__label">${ buttonLabel }</span>
                            ${ ARROW_SVG }
                        </button>
                    `;
                    const newStartBtn = document.getElementById( 'btn-start' );
                    if ( newStartBtn ) newStartBtn.addEventListener( 'click', startHandler );
                }

                openRefillDialog( max );
            });
        }

        // Animate coin counter from 0 to current balance over 1 second
        const coinNumEl = document.getElementById( 'pm-coins-num' );
        if ( coinNumEl )
        {
            const target = getCoins();
            const duration = 1000;
            const startAt = performance.now();
            const tick = ( now ) =>
            {
                const t = Math.min( 1, ( now - startAt ) / duration );
                const eased = 1 - Math.pow( 1 - t, 3 );
                const value = Math.round( target * eased );
                coinNumEl.textContent = formatCoins( value );
                if ( t < 1 )
                {
                    this._coinAnimRaf = requestAnimationFrame( tick );
                }
                else
                {
                    coinNumEl.textContent = formatCoins( target );
                    this._coinAnimRaf = null;
                }
            };
            this._coinAnimRaf = requestAnimationFrame( tick );
        }

        // Fullscreen toggle
        const btnFs = document.getElementById( 'btn-fullscreen' );
        if ( btnFs && document.fullscreenEnabled )
        {
            btnFs.addEventListener( 'click', () =>
            {
                if ( document.fullscreenElement )
                {
                    document.exitFullscreen();
                }
                else
                {
                    document.documentElement.requestFullscreen().then( () =>
                    {
                        if ( screen.orientation && screen.orientation.lock )
                        {
                            screen.orientation.lock( 'portrait' ).catch( () => {} );
                        }
                    }).catch( () => {} );
                }
            });

            this._onFullscreenChange = () =>
            {
                btnFs.innerHTML = document.fullscreenElement ? FULLSCREEN_SVG_OFF : FULLSCREEN_SVG_ON;
            };
            document.addEventListener( 'fullscreenchange', this._onFullscreenChange );
        }
        else if ( btnFs )
        {
            btnFs.style.display = 'none';
        }

        // Tick every second — update hearts, refill timer, button state
        this._timerInterval = setInterval( () =>
        {
            const state = getLivesState();
            const max = getMaxHearts();
            const heartsEl = document.getElementById( 'pm-hearts' );
            const timerEl = document.getElementById( 'pm-hearts-timer' );
            const timerText = document.getElementById( 'pm-hearts-timer-text' );
            const refillNote = document.getElementById( 'pm-refill-note' );
            const refillNoteTime = document.getElementById( 'pm-refill-note-time' );
            const btn = document.getElementById( 'btn-start' );

            // Rebuild heart icons (keep timer span intact)
            if ( heartsEl )
            {
                const timerEl2 = document.getElementById( 'pm-hearts-timer' );
                heartsEl.innerHTML = renderHeartsRow( state.hearts, max ) +
                    ( timerEl2 ? timerEl2.outerHTML : '' );
            }

            const ms = getNextRefillMs();
            const showTmr = state.hearts < max && ms > 0;

            const tEl = document.getElementById( 'pm-hearts-timer' );
            const tText = document.getElementById( 'pm-hearts-timer-text' );
            if ( tEl ) tEl.style.display = showTmr ? '' : 'none';
            if ( tText && showTmr ) tText.textContent = formatRefillTimer( ms );

            if ( refillNoteTime && showTmr ) refillNoteTime.textContent = formatRefillTimer( ms );

            // Update button disabled state
            if ( btn )
            {
                const isNoLives = state.hearts <= 0;
                btn.disabled = isNoLives;
                const labelEl = btn.querySelector( '.pm-play__label' );
                const arrow = btn.querySelector( '.pm-play__arrow' );
                if ( isNoLives )
                {
                    btn.classList.add( 'is-disabled' );
                    if ( labelEl ) labelEl.textContent = 'Out of Lives';
                    if ( arrow ) arrow.remove();
                    if ( refillNote ) refillNote.style.display = showTmr ? '' : 'none';
                }
                else
                {
                    btn.classList.remove( 'is-disabled' );
                    if ( labelEl && labelEl.textContent === 'Out of Lives' )
                    {
                        labelEl.textContent = cleared >= 999 ? 'Play Again' : ( cleared > 0 ? 'Continue' : 'Start' );
                    }
                    if ( !arrow ) btn.insertAdjacentHTML( 'beforeend', ARROW_SVG );
                    if ( refillNote ) refillNote.style.display = 'none';
                }
            }
        }, 1000 );
    }

    async exit()
    {
        if ( this._timerInterval ) clearInterval( this._timerInterval );
        this._timerInterval = null;
        if ( this._coinAnimRaf )
        {
            cancelAnimationFrame( this._coinAnimRaf );
            this._coinAnimRaf = null;
        }
        if ( this._onFullscreenChange )
        {
            document.removeEventListener( 'fullscreenchange', this._onFullscreenChange );
            this._onFullscreenChange = null;
        }
        if ( this._unsubscribeInstall )
        {
            this._unsubscribeInstall();
            this._unsubscribeInstall = null;
        }
        this.app.container.innerHTML = '';
        document.getElementById( 'app' ).classList.remove( 'active' );
    }
}
